/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
    ActionButton,
    AlertDialog,
    AlertDialogContent,
    Card,
    DatePicker,
    Dialog,
    DialogContent,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
} from "../../../../../utils/helpers";
import { useFetchInvoiceNotes } from "../../../../../hooks/queries/billing";
import ListState from "../../../../../components/ListState/ListState";
import {
    IInvoiceNoteDocument,
    IInvoiceNotesResponse,
} from "../../../../../utils/types/billing";
import { ViewEditorText } from "../../../../../components/TextEditor/ViewEditorText/ViewEditorText";
import { useDebounce } from "../../../../../hooks/helpers";
import { useDeleteInvoiceNote } from "../../../hooks/billing.mutations";
import useToast from "../../../../../hooks/useToast";

import DeleteIcon from "../../../../../icons/Delete";
import EditIcon from "../../../../../icons/Edit";
import Loader from "../../../../../shared-ui/Loader/Loader";
import EditInvoiceNoteForm from "../../EditInvoiceNoteForm/EditInvoiceNoteForm";
import ViewInvoiceNoteDoc from "../../ViewInvoiceNoteDoc/ViewInvoiceNoteDoc";

interface InvoiceNotesProps {
    invoiceId: string;
}

type Dialogs = "edit" | "delete";

function InvoiceNotesList({ invoiceId }: InvoiceNotesProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [showDocPreview, setShowDocPreview] = React.useState(false);
    const [invoiceNoteToEdit, setInvoiceNoteToEdit] =
        React.useState<IInvoiceNotesResponse | null>(null);
    const [docInView, setDocInView] =
        React.useState<IInvoiceNoteDocument | null>(null);
    const [selectedInvoiceNoteId, setSelectedInvoiceNoteId] = React.useState<
        string | null
    >(null);

    const [limit, setLimit] = React.useState(20);

    const deleteInvoiceNote = useDeleteInvoiceNote();

    // query client
    const queryClient = useQueryClient();
    const { toast } = useToast();

    // Get filters from params
    const authorFilter = searchParams.get("author") || "";
    const pageFilter = Number(searchParams.get("page")) || 1;
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");

    // use debounce hook for author's name filter
    const authorName = useDebounce(authorFilter || "", 500);

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const isAddInvoiceNoteDocMutating = useIsMutating([
        "add-invoice-note-document",
    ]);
    const editInvoiceNoteMutating = useIsMutating(["edit-invoice-note"]);

    // filter author onchange fn
    const handleAuthorSearchChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("author");
        else searchParams.set("author", e.target.value);

        setSearchParams(searchParams);
    };

    // onChange handler for page
    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    // onChange handler for from date
    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));

        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    // onCHange handler for to date
    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));

        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    // fetch invoice notes
    const { data, isLoading, error, isSuccess } = useFetchInvoiceNotes(
        invoiceId,
        {
            author_name: authorName,
            invoice_id: invoiceId,
            page: pageFilter,
            from_date: fromDateFilter,
            to_date: toDateFilter,
            limit,
        },
        Boolean(invoiceId)
    );

    const handleDeleteInvoiceNote = () => {
        deleteInvoiceNote.mutate(
            {
                invoiceId,
                invoiceNoteId: selectedInvoiceNoteId ?? "",
            },
            {
                onSuccess: async (res) => {
                    await queryClient.invalidateQueries({
                        queryKey: ["invoice-notes", invoiceId],
                    });
                    toast({
                        mode: "success",
                        message:
                            res.message || "Internal note deleted successfully",
                    });
                    setDialogInView(null);
                    setSelectedInvoiceNoteId(null);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete internal note, please try again",
                    });
                },
            }
        );
    };

    const handleShowEditDialog = (invoiceNote: IInvoiceNotesResponse) => {
        setDialogInView("edit");

        setInvoiceNoteToEdit(invoiceNote);
    };

    const handleCloseEditDialog = () => {
        setDialogInView(null);

        setInvoiceNoteToEdit(null);
    };

    const handleCloseDocPreview = () => {
        setShowDocPreview(false);
        setDocInView(null);
    };

    const columns: ColumnDef<IInvoiceNotesResponse>[] = [
        {
            accessorKey: "date_created",
            header: "DATE CREATED",
            cell: ({ row }) => `${formatDate(row.original.date_created)}`,
        },
        {
            accessorKey: "author",
            header: "AUTHOR",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.author.first_name,
                    lastName: row.original.author.last_name,
                }),
        },
        {
            accessorKey: "description",
            header: "NOTE CONTENT",
            cell: ({ row }) => (
                <ViewEditorText text={row.original.description} />
            ),
        },
        {
            accessorKey: "documents",
            header: "Documents",
            cell: ({ row }) =>
                row.original.documents && row.original.documents.length > 0 ? (
                    <div className="text-caption flex flex-col">
                        {row.original.documents.map((doc, i, arr) => (
                            <ActionButton
                                key={doc.invoice_note_document_id}
                                variant="can-hover"
                                size="auto"
                                className="h-7 !text-text text-caption underline"
                                actionTitle="View"
                                onClick={() => {
                                    setShowDocPreview(true);
                                    setDocInView(doc);
                                }}
                            >
                                <span>{`${displayNameInRightFormat(
                                    {
                                        firstName: doc.uploaded_by.first_name,
                                        lastName: doc.uploaded_by.last_name,
                                    },
                                    false
                                )}, ${formatDate(doc.created_at)}${
                                    i + 1 === arr.length ? "" : ","
                                }`}</span>
                            </ActionButton>
                        ))}
                    </div>
                ) : (
                    "--"
                ),
        },
        {
            accessorKey: "invoice_note_id",
            header: "",
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: ({ row }) => (
                <div className="flex items-center self-start">
                    <ActionButton
                        variant="can-hover"
                        size="auto"
                        className="h-7"
                        onClick={() => {
                            setDialogInView("delete");
                            setSelectedInvoiceNoteId(
                                row.original.invoice_note_id
                            );
                        }}
                        actionTitle="Delete note"
                    >
                        <DeleteIcon />
                    </ActionButton>
                    <ActionButton
                        variant="can-hover"
                        size="auto"
                        className="h-7"
                        onClick={() => handleShowEditDialog(row.original)}
                        actionTitle="Edit note"
                    >
                        <EditIcon />
                    </ActionButton>
                </div>
            ),
        },
    ];

    return (
        <>
            <AlertDialog open={dialogInView === "delete"}>
                <AlertDialogContent
                    onSaveClick={handleDeleteInvoiceNote}
                    saveText="Delete Internal Note"
                    onCancelClick={() => setDialogInView(null)}
                    title="Delete Internal Note"
                    description="Are you sure you want to delete this  internal note?"
                    isSaveBtnDisabled={deleteInvoiceNote.isLoading}
                    isCancelBtnDisabled={deleteInvoiceNote.isLoading}
                    isDeleting
                />
            </AlertDialog>
            {invoiceNoteToEdit ? (
                <Dialog open={dialogInView === "edit"}>
                    <DialogContent
                        title="Edit internal note"
                        handleCloseDialog={handleCloseEditDialog}
                        variant="center"
                        showFooter
                        saveText="Edit internal note"
                        cancelText="Cancel"
                        submitBtnFormValue="edit-invoice-note-form"
                        isSubmitBtnDisabled={
                            editInvoiceNoteMutating > 0 ||
                            isAddInvoiceNoteDocMutating > 0
                        }
                        isCancelBtnDisabled={
                            editInvoiceNoteMutating > 0 ||
                            isAddInvoiceNoteDocMutating > 0
                        }
                    >
                        <EditInvoiceNoteForm
                            invoiceNote={invoiceNoteToEdit}
                            onFormSubmit={handleCloseEditDialog}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            {showDocPreview && docInView ? (
                <div>
                    <div className="mb-[--spacing-header]">
                        <ActionButton
                            onClick={handleCloseDocPreview}
                            variant="secondary"
                            actionTitle="Return to list"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="size-5"
                            >
                                <line x1="19" y1="12" x2="5" y2="12" />
                                <polyline points="12 19 5 12 12 5" />
                            </svg>
                        </ActionButton>
                    </div>
                    <ViewInvoiceNoteDoc
                        url={docInView.url}
                        contentType={docInView?.metadata?.content_type ?? ""}
                        title={docInView?.document_name ?? ""}
                    />
                </div>
            ) : (
                <div>
                    <Card classNames="mb-[--spacing-header]">
                        <div className="flex justify-between gap-[--spacing-items] flex-wrap">
                            <SearchInput
                                onChange={handleAuthorSearchChange}
                                value={authorFilter}
                                containerClass="w-[300px] max-w-full"
                                placeholder="Search by Author"
                            />
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            onChange={
                                                handleFromDateFilterChange
                                            }
                                            selected={fromDateFilterToDate}
                                            maxDate={
                                                toDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            disabled={isLoading}
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        onChange={handleToDateFilterChange}
                                        selected={toDateFilterToDate}
                                        minDate={fromDateFilterToDate}
                                        disabled={isLoading}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </Card>

                    <div>
                        {isLoading ? <Loader /> : null}

                        {error && error?.response?.status !== 404 && (
                            <ListState
                                isError
                                stateHelperText="Try reloading this page to solve this issue"
                                errorMsg={
                                    error?.response?.data.message ||
                                    `Cannot display internal notes at this time. Please try again later`
                                }
                            />
                        )}

                        {data && isSuccess && data.data.length === 0 && (
                            <ListState
                                stateHelperText="Internal notes will appear here once they are added"
                                isError={false}
                                emptyMessage="No internal note added yet"
                            />
                        )}

                        {isSuccess && data && data.data.length > 0 ? (
                            <div>
                                <div>
                                    <Table
                                        columns={columns}
                                        data={data.data}
                                        hasPagination={data.total_count > 20}
                                        pagination={
                                            <Pagination
                                                totalCount={data.total_count}
                                                totalPages={data.total_pages}
                                                currentPage={pageFilter}
                                                onPageChange={handlePageChange}
                                                onLimitChange={(l) =>
                                                    setLimit(l)
                                                }
                                                limit={limit}
                                            />
                                        }
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
        </>
    );
}

export default InvoiceNotesList;
